<script lang="ts">
  import { onMount } from 'svelte';
  import { push, replace } from 'svelte-spa-router';
  import * as tus from 'tus-js-client';
  import { session } from '../lib/session';
  import { api, ApiError } from '../lib/api';
  import { adminState } from '../lib/admin-state';
  import DatePicker from './DatePicker.svelte';
  import type { AdminProductDTO, EventStatsDTO } from '../lib/types';

  interface Props {
    eventId?: string | null;
  }
  let { eventId = null }: Props = $props();
  const isEdit = $derived(!!eventId);

  let title = $state('');
  let description = $state('');
  let dateStr = $state(''); // YYYY-MM-DD (local)
  let timeStr = $state('19:00'); // HH:MM (local)
  let durationMin = $state<number | null>(60);
  let streamUrl = $state('');
  let selectedProductIds = $state<string[]>([]);

  let products = $state<AdminProductDTO[]>([]);
  let loading = $state(true);
  let saving = $state(false);
  let deleting = $state(false);
  let error = $state<string | null>(null);

  // Recording (Bunny) — reuse the material upload flow.
  let videoGuid = $state<string | null>(null); // existing or freshly uploaded
  let recordingSec = $state<number | null>(null);
  let file = $state<File | null>(null);
  let uploadProgress = $state<number | null>(null);
  let uploading = $state(false);
  let currentUpload: tus.Upload | null = null;
  let playbackUrl = $state<string | null>(null);
  let playbackGuid: string | null = null;
  const canUpload = $derived(!!$session?.bunnyWritable);

  // Stats (edit only)
  let stats = $state<EventStatsDTO | null>(null);

  const VIDEO_EXTS = [
    '4mv', 'amv', 'avi', 'flv', 'm4v', 'mkv', 'mov',
    'mp4', 'mpeg', 'mpg', 'mxf', 'ts', 'vod', 'webm', 'wmv',
  ];
  const acceptAttr = VIDEO_EXTS.map((e) => '.' + e).join(',') + ',video/quicktime,video/*';

  onMount(async () => {
    if (!$session) {
      replace('/');
      return;
    }
    if (!$session.isAdmin) {
      replace('/home');
      return;
    }
    try {
      const [{ items }, ev] = await Promise.all([
        api.adminProducts(),
        eventId ? api.adminEvent(eventId) : Promise.resolve(null),
      ]);
      products = items;
      if (ev) {
        title = ev.title;
        description = ev.description ?? '';
        const d = new Date(ev.startsAt);
        dateStr = `${d.getFullYear()}-${p2(d.getMonth() + 1)}-${p2(d.getDate())}`;
        timeStr = `${p2(d.getHours())}:${p2(d.getMinutes())}`;
        durationMin = ev.durationMin;
        streamUrl = ev.streamUrl ?? '';
        selectedProductIds = [...ev.productIds];
        videoGuid = ev.bunnyVideoId;
        recordingSec = ev.recordingSec;
        // Load stats in the background.
        api.adminEventStats(eventId!).then((s) => (stats = s)).catch(() => {});
      } else {
        // New event: default to the Club (accessScope='all') products.
        selectedProductIds = items.filter((p) => p.accessScope === 'all').map((p) => p.id);
      }
    } catch (err) {
      error = err instanceof ApiError ? `Не вдалось завантажити (${err.code})` : 'Не вдалось завантажити';
    } finally {
      loading = false;
    }
  });

  const p2 = (n: number): string => String(n).padStart(2, '0');
  const todayStr = (() => {
    const d = new Date();
    return `${d.getFullYear()}-${p2(d.getMonth() + 1)}-${p2(d.getDate())}`;
  })();
  // Block creating an event in the past; editing a past event stays allowed
  // (e.g. to attach the recording after it happened).
  const startsPast = $derived(
    !!dateStr && !!timeStr && new Date(`${dateStr}T${timeStr}:00`).getTime() < Date.now(),
  );

  function toggleProduct(id: string): void {
    selectedProductIds = selectedProductIds.includes(id)
      ? selectedProductIds.filter((x) => x !== id)
      : [...selectedProductIds, id];
  }

  // ── Recording upload ──
  $effect(() => {
    if (!videoGuid) {
      playbackUrl = null;
      playbackGuid = null;
      return;
    }
    if (videoGuid === playbackGuid) return;
    playbackGuid = videoGuid;
    playbackUrl = null;
    api
      .adminBunnyPlayback(videoGuid)
      .then(({ iframeUrl }) => {
        if (playbackGuid === videoGuid) playbackUrl = iframeUrl;
      })
      .catch(() => {});
  });

  function onFileChange(e: Event): void {
    const input = e.target as HTMLInputElement;
    const f = input.files?.[0] ?? null;
    if (!f) return;
    const ext = f.name.split('.').pop()?.toLowerCase() ?? '';
    if (!VIDEO_EXTS.includes(ext)) {
      error = `Непідтримуваний формат «.${ext}» для відео.`;
      input.value = '';
      return;
    }
    error = null;
    file = f;
    try {
      const url = URL.createObjectURL(f);
      const el = document.createElement('video');
      el.preload = 'metadata';
      el.src = url;
      el.onloadedmetadata = () => {
        if (Number.isFinite(el.duration) && el.duration > 0) recordingSec = Math.round(el.duration);
        URL.revokeObjectURL(url);
      };
    } catch {
      /* ignore */
    }
    startUpload(f);
  }

  function startUpload(f: File): void {
    cancelUpload();
    uploading = true;
    uploadProgress = 0;
    api
      .adminBunnyUploadCredentials(title.trim() || f.name, undefined)
      .then((creds) => {
        currentUpload = new tus.Upload(f, {
          endpoint: 'https://video.bunnycdn.com/tusupload',
          // Chunks keep each PATCH short so a dropped connection doesn't leave the
          // upload locked on Bunny (423 "upload is currently locked" on retry).
          chunkSize: 50 * 1024 * 1024,
          retryDelays: [0, 3000, 5000, 10000, 20000, 30000, 60000, 60000, 120000],
          headers: {
            AuthorizationSignature: creds.signature,
            AuthorizationExpire: String(creds.expirationTime),
            VideoId: creds.videoGuid,
            LibraryId: creds.libraryId,
          },
          metadata: { filetype: f.type, title: title.trim() || f.name },
          onError(err) {
            uploading = false;
            uploadProgress = null;
            currentUpload = null;
            error = 'Помилка завантаження в Bunny: ' + (err instanceof Error ? err.message : 'невідома');
          },
          onProgress(bytesUploaded, bytesTotal) {
            uploadProgress = Math.round((bytesUploaded / bytesTotal) * 100);
          },
          onSuccess() {
            uploading = false;
            uploadProgress = 100;
            videoGuid = creds.videoGuid;
            currentUpload = null;
          },
        });
        currentUpload.start();
      })
      .catch((err) => {
        uploading = false;
        uploadProgress = null;
        error =
          err instanceof ApiError
            ? `Не вдалось отримати credentials (${err.code})`
            : 'Не вдалось почати завантаження';
      });
  }

  function cancelUpload(): void {
    if (currentUpload) {
      try {
        currentUpload.abort(true);
      } catch {
        /* ignore */
      }
      currentUpload = null;
    }
  }

  function removeRecording(): void {
    cancelUpload();
    file = null;
    recordingSec = null;
    uploadProgress = null;
    videoGuid = null;
  }

  const canSave = $derived(
    title.trim().length > 0 && !!dateStr && !!timeStr && !uploading && (isEdit || !startsPast),
  );

  async function save(): Promise<void> {
    if (!canSave || saving) return;
    saving = true;
    error = null;
    // Combine local date + time → ISO instant.
    const startsAt = new Date(`${dateStr}T${timeStr}:00`).toISOString();
    const body = {
      title: title.trim(),
      description: description.trim() || null,
      startsAt,
      durationMin: durationMin ?? null,
      streamUrl: streamUrl.trim() || null,
      bunnyVideoId: videoGuid,
      recordingSec: videoGuid ? recordingSec ?? null : null,
      productIds: selectedProductIds,
    };
    try {
      if (eventId) await api.adminPatchEvent(eventId, body);
      else await api.adminCreateEvent(body);
      adminState.activeTab = 'schedule';
      push('/admin/dashboard');
    } catch (err) {
      error = err instanceof ApiError ? `Не вдалось зберегти (${err.code})` : 'Не вдалось зберегти';
    } finally {
      saving = false;
    }
  }

  async function remove(): Promise<void> {
    if (!eventId || deleting) return;
    if (!confirm('Видалити подію?')) return;
    deleting = true;
    error = null;
    try {
      await api.adminDeleteEvent(eventId);
      adminState.activeTab = 'schedule';
      push('/admin/dashboard');
    } catch (err) {
      error = err instanceof ApiError ? `Не вдалось видалити (${err.code})` : 'Не вдалось видалити';
      deleting = false;
    }
  }
</script>

<div class="screen">
  <div class="admin-header">
    <a href="#/admin/dashboard" class="back-link">← Назад</a>
    <div class="header-title">{isEdit ? 'Редагувати подію' : 'Нова подія'}</div>
    <span class="header-spacer"></span>
  </div>

  <div class="scroll">
    {#if loading}
      <div class="placeholder">Завантажуємо…</div>
    {:else}
      <label class="field-label">Назва</label>
      <input class="field" maxlength="160" placeholder="Напр. Зустріч простору: розбір питань" bind:value={title} />

      <label class="field-label">Опис (необовʼязково)</label>
      <textarea class="field" maxlength="4000" placeholder="Про що зустріч…" bind:value={description}></textarea>

      <div class="row2">
        <div>
          <label class="field-label">Дата</label>
          <DatePicker value={dateStr} min={isEdit ? undefined : todayStr} onChange={(v) => (dateStr = v)} />
        </div>
        <div>
          <label class="field-label">Час</label>
          <input class="field time-field" type="time" bind:value={timeStr} />
        </div>
      </div>
      {#if !isEdit && startsPast}
        <div class="hint warn">Не можна створити подію в минулому — оберіть майбутні дату й час.</div>
      {/if}

      <label class="field-label">Тривалість (хв)</label>
      <input class="field" type="number" min="0" placeholder="60" bind:value={durationMin} />

      <label class="field-label">Посилання на трансляцію</label>
      <input class="field" placeholder="https://zoom.us/j/…" bind:value={streamUrl} />
      <div class="hint">Показується користувачам лише за наявності доступу; клік логуємо для статистики.</div>

      <label class="field-label">Доступ — яким продуктам</label>
      {#if products.length === 0}
        <div class="placeholder">Немає продуктів</div>
      {:else}
        <div class="cat-list">
          {#each products as p (p.id)}
            <!-- svelte-ignore a11y_label_has_associated_control -->
            <label class="cat-row" class:checked={selectedProductIds.includes(p.id)}>
              <input
                type="checkbox"
                checked={selectedProductIds.includes(p.id)}
                onchange={() => toggleProduct(p.id)}
              />
              <span class="cat-name">{p.title}</span>
              {#if p.accessScope === 'all'}<span class="cat-tag">Простір</span>{/if}
            </label>
          {/each}
        </div>
      {/if}

      <label class="field-label">Запис зустрічі (необовʼязково)</label>
      {#if !canUpload}
        <div class="hint">Завантаження відео недоступне в цьому середовищі (read-only).</div>
      {:else if videoGuid && playbackUrl && !uploading}
        <div class="video-wrap">
          <iframe title="Запис" src={playbackUrl} allow="autoplay; fullscreen" allowfullscreen></iframe>
        </div>
        <button type="button" class="mini-btn" onclick={removeRecording}>Прибрати запис</button>
      {:else if uploading}
        <div class="upload-progress">Завантаження… {uploadProgress ?? 0}%</div>
      {:else}
        <label class="upload-drop">
          <input type="file" accept={acceptAttr} onchange={onFileChange} hidden />
          <span>＋ Обрати відео-файл</span>
        </label>
      {/if}

      {#if isEdit && stats}
        <div class="stats-card">
          <div class="stats-title">Статистика</div>
          <div class="stats-row"><span>Переходів за посиланням</span><b>{stats.totalClicks}</b></div>
          <div class="stats-row"><span>Учасників (унікальні)</span><b>{stats.uniqueParticipants}</b></div>
          <div class="stats-row"><span>Переглянули запис</span><b>{stats.recordingViewers}</b></div>
          {#if stats.participants.length > 0}
            <div class="stats-sub">Учасники</div>
            {#each stats.participants as pt (pt.telegramId)}
              <a class="part-row" href={`#/admin/user/${pt.telegramId}`}>
                <span class="part-name">{pt.fullName}</span>
                <span class="part-meta">{pt.clicksCount}×</span>
              </a>
            {/each}
          {/if}
        </div>
      {/if}

      {#if error}<div class="error">{error}</div>{/if}

      <button type="button" class="submit" disabled={!canSave || saving} onclick={save}>
        {saving ? 'Зберігаємо…' : isEdit ? 'Зберегти зміни' : 'Створити подію'}
      </button>
      {#if isEdit}
        <button type="button" class="delete" disabled={deleting} onclick={remove}>
          {deleting ? 'Видаляємо…' : 'Видалити подію'}
        </button>
      {/if}
    {/if}
  </div>
</div>

<style>
  .screen { display: flex; flex-direction: column; flex: 1; min-height: 0; background: var(--bg); }
  .admin-header {
    background: linear-gradient(135deg, var(--dark), var(--dark3));
    padding: 14px 18px; color: var(--text-light); display: flex; align-items: center; gap: 10px; flex-shrink: 0;
  }
  .back-link { font-size: 12px; color: rgba(251, 246, 236, 0.9); text-decoration: none; }
  .header-title { flex: 1; text-align: center; font-size: 14px; font-weight: 600; }
  .header-spacer { width: 40px; }
  .scroll { flex: 1; overflow-y: auto; padding: 18px 18px 100px; }
  .placeholder {
    padding: 16px; text-align: center; color: var(--text-muted); font-size: 13px;
    background: var(--bg2); border: 1px dashed var(--border2); border-radius: 10px;
  }
  .field-label {
    display: block; font-size: 11px; font-weight: 600; color: var(--text-muted);
    margin: 14px 0 6px; letter-spacing: 0.5px; text-transform: uppercase;
  }
  .field {
    width: 100%; border: 1px solid var(--border2); border-radius: 10px; padding: 11px 13px;
    font-size: 13px; color: var(--text); background: var(--bg); font-family: inherit;
    outline: none; margin-bottom: 4px;
  }
  .field:focus { border-color: var(--outline); }
  textarea.field { resize: vertical; min-height: 70px; }
  .hint { font-size: 11px; color: var(--text-muted); margin: 4px 2px 0; line-height: 1.4; }
  .hint.warn { color: var(--status-inactive); font-weight: 600; }
  /* Top-align columns so the ДАТА / ЧАС labels sit on the same line regardless
     of any tiny field-height difference. */
  .row2 { display: flex; gap: 10px; align-items: flex-start; }
  .row2 > div { flex: 1; min-width: 0; }
  /* Match the DatePicker's .dp-field metrics so date + time look consistent. */
  .time-field { padding: 8px 10px; font-size: 12px; font-weight: 600; border-radius: 8px; margin-bottom: 0; }

  .cat-list { display: flex; flex-direction: column; gap: 6px; }
  .cat-row {
    display: flex; align-items: center; gap: 10px; padding: 10px 12px;
    background: var(--bg2); border: 1px solid var(--border); border-radius: 10px; cursor: pointer;
  }
  .cat-row.checked { border-color: var(--outline); background: var(--pu-light); }
  .cat-row input { width: 17px; height: 17px; accent-color: var(--pu); }
  .cat-name { flex: 1; font-size: 13px; font-weight: 600; color: var(--text); }
  .cat-tag {
    font-size: 10px; font-weight: 600; padding: 2px 7px; border-radius: 6px;
    background: var(--pu); color: var(--text-light);
  }

  .video-wrap { position: relative; width: 100%; aspect-ratio: 16/9; border-radius: 12px; overflow: hidden; background: #000; }
  .video-wrap iframe { position: absolute; inset: 0; width: 100%; height: 100%; border: 0; }
  .mini-btn {
    margin-top: 8px; border: 1px solid var(--border2); background: var(--bg); color: var(--text-muted);
    padding: 7px 12px; border-radius: 8px; font-size: 12px; cursor: pointer; font-family: inherit;
  }
  .upload-drop {
    display: flex; align-items: center; justify-content: center; padding: 22px;
    border: 1px dashed var(--pu2); border-radius: 12px; background: var(--bg2);
    color: var(--accent-text); font-size: 13px; font-weight: 600; cursor: pointer;
  }
  .upload-progress {
    padding: 16px; text-align: center; background: var(--pu-light); border: 1px solid var(--border);
    border-radius: 12px; color: var(--accent-text); font-size: 13px; font-weight: 600;
  }

  .stats-card { margin-top: 18px; border: 1px solid var(--border); border-radius: 12px; padding: 14px; }
  .stats-title { font-size: 12px; font-weight: 700; text-transform: uppercase; color: var(--text-muted); margin-bottom: 10px; }
  .stats-row { display: flex; justify-content: space-between; font-size: 13px; color: var(--text-muted); padding: 5px 0; }
  .stats-row b { color: var(--text); font-size: 15px; }
  .stats-sub { font-size: 11px; font-weight: 700; text-transform: uppercase; color: var(--text-muted); margin: 12px 0 6px; }
  .part-row {
    display: flex; justify-content: space-between; align-items: center; gap: 8px;
    padding: 8px 10px; border: 1px solid var(--border); border-radius: 10px; margin-bottom: 6px;
    text-decoration: none;
  }
  .part-name { font-size: 13px; color: var(--text); font-weight: 600; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .part-meta { font-size: 11px; color: var(--text-muted); flex-shrink: 0; }

  .error { margin: 12px 0; padding: 10px 14px; background: var(--danger-bg); color: var(--status-inactive); border: 1px solid var(--danger-border); border-radius: 10px; font-size: 12px; }
  .submit {
    width: 100%; margin-top: 18px; padding: 14px; border: none; border-radius: 12px;
    background: linear-gradient(135deg, var(--pu), var(--pu3)); color: var(--text-light);
    font-size: 14px; font-weight: 600; cursor: pointer; font-family: inherit;
  }
  .submit:disabled { opacity: 0.5; cursor: not-allowed; }
  .delete {
    width: 100%; margin-top: 10px; padding: 13px; border: 1px solid var(--danger-border); border-radius: 12px;
    background: var(--bg); color: var(--status-inactive); font-size: 13px; font-weight: 600; cursor: pointer; font-family: inherit;
  }
  .delete:disabled { opacity: 0.5; cursor: not-allowed; }
</style>
